import React, { createContext, useContext, useState, useEffect } from 'react';
import { authService } from '../api';
import config from '../config';

const AuthContext = createContext(null);

/* 더미 모드용 유저 정보 (localStorage 기반) */
function getDummyUser() {
  return {
    id: 1,
    name: localStorage.getItem('userName') || '홍길동',
    email: localStorage.getItem('userEmail') || 'hong@email.com',
    role: localStorage.getItem('userRole') || 'USER',
  };
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('accessToken');
    if (token) {
      if (config.USE_DUMMY) {
        setUser(getDummyUser());
        setLoading(false);
      } else {
        authService.getMe()
          .then((res) => {
            const userData = res.data || res;
            setUser(userData);
          })
          .catch(() => {
            localStorage.removeItem('accessToken');
            localStorage.removeItem('refreshToken');
            localStorage.removeItem('userRole');
          })
          .finally(() => setLoading(false));
      }
    } else {
      setLoading(false);
    }
  }, []);

  const login = async (credentials) => {
    if (config.USE_DUMMY) {
      /* 더미 모드: 아무 이메일/비밀번호로 로그인 성공 */
      const token = 'demo-token';
      localStorage.setItem('accessToken', token);
      localStorage.setItem('userName', '홍길동');
      localStorage.setItem('userEmail', credentials?.email || 'hong@email.com');
      localStorage.setItem('userRole', 'USER');
      const userData = getDummyUser();
      setUser(userData);
      return userData;
    }
    const res = await authService.login(credentials);
    localStorage.setItem('accessToken', res.access_token);
    if (res.refresh_token) localStorage.setItem('refreshToken', res.refresh_token);
    localStorage.setItem('userRole', res.role?.toUpperCase() || 'USER');
    localStorage.setItem('userId', res.user_id);
    // 로그인 응답엔 id/role 만 있어 user.name/phone/email 이 비어 있다 → 예약자 정보 자동기입
    //   (BookerInfoCard 가 user 의 name/phone/email 로 채움)이 새로고침 전엔 동작하지 않음.
    //   getMe 로 전체 프로필을 즉시 보강해 부트스트랩(새로고침) 시점과 동일한 user 를 보장한다.
    //   id/role 은 로그인 응답값을 그대로 유지(역할 분기 회귀 방지). 보강 실패해도 로그인은 성립.
    let userData = { id: res.user_id, role: res.role };
    try {
      const me = await authService.getMe();
      userData = { ...(me.data || me), id: res.user_id, role: res.role };
    } catch { /* 프로필 보강 실패 시 최소 {id, role} 유지 */ }
    setUser(userData);
    return userData;
  };

  const socialLogin = async (provider, accessToken, extra = {}) => {
    if (config.USE_DUMMY) {
      const token = 'demo-token';
      localStorage.setItem('accessToken', token);
      localStorage.setItem('userRole', 'USER');
      const userData = getDummyUser();
      setUser(userData);
      return userData;
    }
    const body = accessToken ? { access_token: accessToken } : extra;
    const res = await authService.oAuth(provider, body);
    localStorage.setItem('accessToken', res.access_token);
    if (res.refresh_token) localStorage.setItem('refreshToken', res.refresh_token);
    localStorage.setItem('userRole', res.role?.toUpperCase() || 'USER');
    localStorage.setItem('userId', res.user_id);
    // 소셜 로그인도 동일 — 응답엔 id/role 만 있어 예약자 자동기입용 name/phone/email 이 비어 있다.
    //   getMe 로 전체 프로필 즉시 보강(새로고침 시점과 동일 user 보장). id/role 은 응답값 유지.
    let userData = { id: res.user_id, role: res.role };
    try {
      const me = await authService.getMe();
      userData = { ...(me.data || me), id: res.user_id, role: res.role };
    } catch { /* 프로필 보강 실패 시 최소 {id, role} 유지 */ }
    setUser(userData);
    return userData;
  };

  // 로컬 세션 즉시 정리 — BE 호출 없음.
  // 회원탈퇴 직후 호출 시 사용: 탈퇴된 user 로 BE /logout 호출하면 401 → client.js
  // interceptor 가 redirect 시작해 page unload 가 일어나며 일부 localStorage 항목이
  // 정리되지 않은 채로 끊김 → 새로고침 시 stale 토큰으로 자동 로그인되는 회귀 방지.
  const clearAuth = () => {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('userName');
    localStorage.removeItem('userEmail');
    localStorage.removeItem('userRole');
    localStorage.removeItem('userId');
    // 로그아웃/회원탈퇴 시 비회원 예약조회 캐시도 정리 — 이전 사용자의 이메일·예약 정보가
    // 그대로 복원되어 노출되던 문제 방지(GuestReservation 이 이 키들로 상태 복원함).
    sessionStorage.removeItem('guestReservation');
    localStorage.removeItem('guestAccessToken');
    setUser(null);
  };

  const logout = async () => {
    if (!config.USE_DUMMY) {
      await authService.logout().catch(() => {});
    }
    clearAuth();
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, socialLogin, logout, clearAuth }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
